import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Loader, SocialContainer, Link } from 'components';
import TextInput from 'components/Inputs/TextInput/TextInputV2';
import { FIELDS } from './form';
import { useSignIn } from './hooks';
import { getAuthLoginLoading } from 'store/selectors/auth';
import { ReactComponent as RejectIcon } from 'static/icons/reject.svg';

import {
  StyledButton,
  ActionContainer,
  ForgotPassword,
  Header,
  ErrorMessage,
  StyledForm,
  StyledGumbLogo,
} from '../styled';
import { useLocation, useSearchParams } from 'react-router-dom';

const SignInPage = () => {
  const { t } = useTranslation();
  const {
    register,
    errors,
    reset,
    onSubmit,
    authError,
    clearAuthError,
    showSignupCta,
    clearSignupCta,
    isPrecheckLoading,
    highlightProviders,
    clearHighlights,
  } = useSignIn();
  const isLoginLoading = useSelector(getAuthLoginLoading);
  const [isSocialLoading, setIsSocialLoading] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Capture Google Analytics parameters
  const gaParams = useMemo(() => {
    const params = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('_g')) {
        params.append(key, value);
      }
    }
    return params.toString();
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('u')) {
      reset({
        [FIELDS.EMAIL]: searchParams.get('u'),
      });
    }
  }, [searchParams]);

  const searchParamsString = useMemo(() => {
    const queryStringArr = [];
    const pathNameArr = location.pathname.split('/');

    if (pathNameArr[1] === 'communities' || pathNameArr[1] === 'community') {
      queryStringArr.push(`invitationId=${pathNameArr[2]}`);
    }

    const invitationCode = searchParams.get('invitationCode');

    if (invitationCode) {
      queryStringArr.push(`invitationCode=${invitationCode}`);
    }

    // Add Google Analytics parameters if they exist
    if (gaParams) {
      queryStringArr.push(gaParams);
    }

    return queryStringArr.join('&');
  }, [location, gaParams]);

  const isSubmitting = isLoginLoading || isPrecheckLoading;
  const signupUrl = `/signup?${searchParamsString}`;

  return (
    <StyledForm onSubmit={onSubmit}>
      <StyledGumbLogo />
      <Header>{t('auth.welcome')}</Header>
      {authError ? (
        <ErrorMessage>
          <RejectIcon
            width={16}
            height={16}
            aria-hidden="true"
            focusable="false"
            style={{ flex: '0 0 auto' }}
          />
          <span>
            {showSignupCta ? (
              <Trans
                i18nKey="auth.userDoesNotExist"
                components={[
                  <Link
                    key="signup"
                    to={signupUrl}
                    onClick={() => {
                      clearAuthError();
                      clearSignupCta();
                    }}
                  />,
                ]}
              />
            ) : (
              authError
            )}
          </span>
        </ErrorMessage>
      ) : null}
      <TextInput
        error={errors[FIELDS.EMAIL]}
        register={register}
        name={FIELDS.EMAIL}
        label={t('auth.labels.email')}
      />
      <TextInput
        error={errors[FIELDS.PASSWORD]}
        register={register}
        name={FIELDS.PASSWORD}
        label={t('auth.labels.password')}
        type="password"
        withShowIcon
      />
      <StyledButton
        variant={isSubmitting ? 'secondary' : null}
        disabled={isSubmitting || isSocialLoading}
      >
        {isSubmitting ? (
          <Loader type="button" size="28px" thickness={2} />
        ) : (
          t('auth.signIn')
        )}
      </StyledButton>
      <ActionContainer>
        <Link to={signupUrl}>{t('auth.create')}</Link>
        <ForgotPassword to={`forgotpassword${gaParams ? `?${gaParams}` : ''}`}>
          {t('auth.forgotPassword')}
        </ForgotPassword>
      </ActionContainer>
      <SocialContainer
        disabled={isSubmitting || isSocialLoading}
        highlightProviders={highlightProviders}
        onAuthStart={() => {
          setIsSocialLoading(true);
          clearAuthError();
          clearHighlights();
        }}
      />
    </StyledForm>
  );
};

export default SignInPage;
